import { useCallback, useEffect, useState } from 'react'

interface VimeoChapter {
  startTime: number
  title: string
  index: number
}

interface VimeoChaptersProps {
  readonly videoUrl: string
  readonly vimeoLoaded: boolean
}

// Extend Window interface to include Vimeo
declare global {
  interface Window {
    Vimeo?: any
  }
}

export default function VimeoChapters({ videoUrl, vimeoLoaded = false }: VimeoChaptersProps) {
  const [chapters, setChapters] = useState<VimeoChapter[]>([])
  const [player, setPlayer] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Format seconds to MM:SS
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = Math.floor(seconds % 60)
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const handleChapterClick = async (startTime: number) => {
    if (player) {
      try {
        await player.setCurrentTime(startTime)
        await player.play()
      } catch (error) {
        console.error('Error during chapter navigation:', error)
      }
    }
  }

  // Initialize Vimeo player and get chapters
  const initPlayer = useCallback(async () => {
    try {
      setLoading(true)

      // Find the iframe in the DOM
      let iframe: HTMLIFrameElement | null = null
      iframe = document.querySelector(`iframe[src*="${videoUrl}"]`)
      if (iframe) {
        // Initialize the player
        const { Vimeo } = window as any
        const vimeoPlayer = new Vimeo.Player(iframe)
        setPlayer(vimeoPlayer)

        // Get chapters
        const chaptersList = await vimeoPlayer.getChapters()
        setChapters(chaptersList)
        setLoading(false)
      }
    } catch (err) {
      console.error('Error initializing Vimeo player or getting chapters:', err)
      setError('Failed to load video chapters')
      setLoading(false)
    }
  }, [videoUrl])

  useEffect(() => {
    // Initialize player when Vimeo SDK is loaded
    if (vimeoLoaded && typeof window !== 'undefined' && window.Vimeo) {
      initPlayer()
    }

    return () => {
      player?.destroy()
    }
  }, [videoUrl, initPlayer, vimeoLoaded])

  if (loading) {
    return <div className="text-sm">Loading chapters...</div>
  }

  if (error) {
    return <div className="text-sm text-warning">{error}</div>
  }

  if (chapters.length === 0) {
    return <div className="text-sm">No chapters found in this video.</div>
  }

  return (
    <ul className="space-y-2">
      {chapters.map((chapter) => (
        <li key={chapter.index} className="flex items-center">
          <button
            onClick={() => handleChapterClick(chapter.startTime)}
            className="flex items-center text-left hover:bg-gray-100 dark:hover:bg-gray-800 w-full px-2 py-1 rounded"
          >
            <span className="text-sm font-mono text-gray-500 w-12">{formatTime(chapter.startTime)}</span>
            <span className="ml-2">{chapter.title}</span>
          </button>
        </li>
      ))}
    </ul>
  )
}
